import { useCallback } from 'react';

import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';

import { AllocationGroupEfficiencySummaryResponse } from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';

const ALLOCATION_GROUPS_EFFICIENCY_SUMMARY_QUERY_KEY =
  'allocation-groups-efficiency-summary';

type UseAllocationGroupsSummaryQuery = {
  data?: AllocationGroupEfficiencySummaryResponse;
  isLoading: boolean;
  error?: unknown;
  refetch?: () => void;
  invalidate(): void;
};

type UseAllocationGroupsSummaryQueryProps = {
  startTime: string;
  endTime: string;
  useListingPrices: boolean;
  clusterIds?: string[];
  enabled?: boolean;
};

const getAllocationGroupsEfficiencySummaryQueryKey = ({
  startTime,
  endTime,
  useListingPrices,
  clusterIds,
}: Partial<
  Pick<
    UseAllocationGroupsSummaryQueryProps,
    'startTime' | 'endTime' | 'useListingPrices' | 'clusterIds'
  >
> = {}) =>
  [
    ALLOCATION_GROUPS_EFFICIENCY_SUMMARY_QUERY_KEY,
    ...(startTime !== undefined
      ? [startTime, endTime, useListingPrices, clusterIds]
      : []),
  ] as const;

const getAllocationGroupsEfficiencySummaryQueryOptions = ({
  startTime,
  endTime,
  useListingPrices,
  clusterIds,
  enabled = true,
}: UseAllocationGroupsSummaryQueryProps) =>
  queryOptions({
    queryKey: getAllocationGroupsEfficiencySummaryQueryKey({
      startTime,
      endTime,
      useListingPrices,
      clusterIds,
    }),
    queryFn: async () => {
      const { data } =
        await apiClient.costReport.getAllocationGroupEfficiencySummary({
          clusterIds,
          startTime,
          endTime,
          useListingPrices,
        });
      return data;
    },
    enabled: Boolean(enabled && startTime && endTime),
    refetchOnWindowFocus: false,
  });

export const useAllocationGroupsEfficiencySummaryQuery = (
  props: UseAllocationGroupsSummaryQueryProps
): UseAllocationGroupsSummaryQuery => {
  const queryClient = useQueryClient();
  const { isLoading, data, error, refetch } = useQuery(
    getAllocationGroupsEfficiencySummaryQueryOptions(props)
  );

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: getAllocationGroupsEfficiencySummaryQueryKey(),
    });
  }, [queryClient]);

  return {
    data,
    isLoading,
    error,
    refetch,
    invalidate,
  };
};
